/**
 * Global ICP link interceptor.
 *
 * A single capture-phase click listener is the one chokepoint for icp
 * propagation: it runs before Vike's client-router click handler, so rewriting
 * the anchor's href in place makes both SPA navigation and native full-loads
 * carry the current icp. This covers every internal `<a>` — both Link
 * components and hand-written anchors — without per-link changes and without a
 * render-time href mutation (so no hydration mismatch).
 */

import { currentIcp, mergeIcp } from "./icp";

let installed = false;

export function installIcpLinkInterceptor() {
  if (installed || typeof document === "undefined") return;
  installed = true;

  document.addEventListener(
    "click",
    (e) => {
      // Only plain primary-button clicks. Modified clicks (new tab/window) and
      // non-left buttons fall through to the browser untouched.
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }

      const anchor = (e.target as Element | null)?.closest?.("a[href]") as
        | HTMLAnchorElement
        | null;
      if (!anchor || anchor.hasAttribute("download")) return;

      const icp = currentIcp();
      if (!icp) return;

      // anchor.href is always absolute; mergeIcp gates same-origin + protocol
      // and is a no-op when icp is absent, external, or already present.
      const next = mergeIcp(anchor.href, icp, window.location.origin);
      if (next !== anchor.href) anchor.href = next;
    },
    true,
  );
}
